import { ReactElement, useState } from "react";
import {
	Dialog,
	Form,
	FormItem,
	TextArea,
	ValidationError,
	FormattedText,
	Toolbar
} from "client/Components";
import {
	makeUserRequest,
	MakeUserRequestItemModel,
	MakeUserRequestModel,
	UserRequestKind
} from "client/Api/UserRequests/userRequest";
import { localize } from "client_legacy/ndaw/culture";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { AlertManager, useAlerts} from "client/Hooks";


type FormModel = {
	reasonForRequest: string;
};

const validationConfiguration: IValidationConfiguration<FormModel> = createValidationConfiguration<FormModel>();

validationConfiguration.forScalar("reasonForRequest")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export type MakeUserRequestDialogProps = {
	item: UserRequestItem;
	onClose(): void;
	onRequested(): void;
};

export const MakeUserRequestDialog = (props: MakeUserRequestDialogProps): ReactElement => {
	const {
		item,
		onClose,
		onRequested
	} = props;

	const [isInteractingWithTheBackend, setIsInteractingWithTheBackend] = useState<boolean>(false);
	const alertManager: AlertManager = useAlerts();

	const [model, setModel] = useState<FormModel>({
		reasonForRequest: ""
	});

	const formHandler: IFormHandler<FormModel> = useFormHandler<FormModel>(
		model,
		setModel,
		validationConfiguration
	);

	const requestHandler = async (): Promise<void> => {
		const { reasonForRequest } = formHandler.reducer.value;

		const validationResult: InputValidationResult = await formHandler.validation.validate();
		if (validationResult.kind === InputValidationResultKind.Invalid) {
			return;
		}

		try {
			setIsInteractingWithTheBackend(true);

			const model: MakeUserRequestModel = {
				reasonForRequest,
				item: getItemModel(item)
			};

			await makeUserRequest(model);

			onRequested();
		} catch (e) {
			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
			setIsInteractingWithTheBackend(false);
		}
	};

	return (
		<Dialog
			trackingId="make-user-request-dialog"
			hasBusyIndicator={isInteractingWithTheBackend}
		>
			<Dialog.Header
				title={getDialogTitle(item)}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={requestHandler}>
					<FormItem
						label={localize("_ReasonForRequest")}
						isRequired
					>
						<TextArea {...formHandler.for("reasonForRequest").attachToTextArea()} autoFocus />
						<ValidationError {...formHandler.for("reasonForRequest").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Request")}
						onClick={requestHandler}
						trackingId="request"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
MakeUserRequestDialog.dislayName = "MakeUserRequestDialog";

function getDialogTitle(item: UserRequestItem): ReactElement {
	switch (item.kind)
	{
		case UserRequestKind.AppAccess:
			return (
				<FormattedText
					text={localize("_RequestNameApp")}
					thingsToReplace={[
						{ what: "{{Name}}", filler: item.name }
					]}
				/>
			);
		default:
			throw new UnreachableCaseError(item.kind);
	}
}

function getItemModel(item: UserRequestItem): MakeUserRequestItemModel {
	switch (item.kind)
	{
		case UserRequestKind.AppAccess:
			return {
				app: {
					id: item.id
				}
			};
		default:
			throw new UnreachableCaseError(item.kind);
	}
}

export type AppAccess = {
	kind: UserRequestKind.AppAccess;
	id: number;
	name: string;
};

export type UserRequestItem = AppAccess;